import React, { useState } from "react";

const ApiDocsLanding = () => {
  const [activeTab, setActiveTab] = useState("getting-started");
  const [copiedCode, setCopiedCode] = useState(null);

  // Detect environment for API URLs (api.localhost for local, api.vmass.vn for production)
  const hostname = window.location.hostname;
  const isLocal =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".localhost") ||
    hostname === "api.localhost";
  const API_BASE_URL = isLocal
    ? "http://localhost:5000"
    : "https://server.vmass.vn";
  const SWAGGER_URL = `${API_BASE_URL}/api/public/docs`;

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="api-docs-container">
      {/* Header */}
      <header className="api-docs-header">
        <div className="header-content">
          <div className="logo">
            <img
              src="/assets/img/logo-horizontal.svg"
              alt="VMASS"
              className="logo-img"
            />
            <span className="logo-api-suffix">API</span>
          </div>
          <nav className="header-nav">
            <a
              href="#getting-started"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("getting-started");
              }}
            >
              Bắt đầu
            </a>
            <a
              href="#endpoints"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("endpoints");
              }}
            >
              Endpoints
            </a>
            <a
              href="#examples"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("examples");
              }}
            >
              Ví dụ
            </a>
            <a
              href="#faq"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("faq");
              }}
            >
              FAQ
            </a>
            <a
              href={SWAGGER_URL}
              className="btn-swagger"
              target="_blank"
              rel="noopener noreferrer"
            >
              🔧 Try API
            </a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1>Tích hợp VMASS vào hệ thống của bạn</h1>
          <p className="hero-subtitle">
            API đơn giản, mạnh mẽ để quản lý đơn hàng, sản phẩm, kho và khách
            hàng.
            <br />
            Được thiết kế cho mọi người - không cần là lập trình viên chuyên
            nghiệp!
          </p>
          <div className="hero-buttons">
            <button
              className="api-btn-primary"
              onClick={() => scrollToSection("getting-started")}
            >
              📖 Hướng dẫn từng bước
            </button>
            <a
              href={SWAGGER_URL}
              className="api-btn-secondary"
              target="_blank"
              rel="noopener noreferrer"
            >
              🧪 Thử ngay trên Swagger
            </a>
          </div>
        </div>
        <div className="hero-illustration">
          <div className="code-preview">
            <div className="code-header">
              <span className="dot red"></span>
              <span className="dot yellow"></span>
              <span className="dot green"></span>
              <span className="filename">Tạo đơn hàng mới</span>
            </div>
            <pre className="code-body">
              {`POST /api/public/v1/orders
X-API-Key: vmss_live_xxx

{
  "items": [
    { "product_id": 1, "quantity": 2, "price": 50000 }
  ],
  "phone": "0901234567"
}`}
            </pre>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="stats-section">
        <div className="stat-card">
          <span className="stat-icon">📦</span>
          <span className="stat-number">7+</span>
          <span className="stat-label">API Modules</span>
        </div>
        <div className="stat-card">
          <span className="stat-icon">⚡</span>
          <span className="stat-number">&lt;100ms</span>
          <span className="stat-label">Response Time</span>
        </div>
        <div className="stat-card">
          <span className="stat-icon">🔒</span>
          <span className="stat-number">100%</span>
          <span className="stat-label">Secure</span>
        </div>
        <div className="stat-card">
          <span className="stat-icon">🧪</span>
          <span className="stat-number">Sandbox</span>
          <span className="stat-label">Test môi trường</span>
        </div>
      </section>

      {/* Getting Started Section */}
      <section id="getting-started" className="section getting-started">
        <h2 className="section-title">🚀 Bắt đầu trong 5 phút</h2>
        <p className="section-subtitle">
          Làm theo 4 bước đơn giản để gọi API đầu tiên của bạn
        </p>

        <div className="steps-container">
          {/* Step 1 */}
          <div className="step-card">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>Lấy API Key</h3>
              <p>
                Đăng nhập vào VMASS Dashboard → <strong>Cài đặt</strong> →{" "}
                <strong>API Keys</strong>
              </p>
              <div className="step-visual">
                <div className="key-example">
                  <div className="key-type test">
                    <span className="key-badge">TEST</span>
                    <code>vmss_test_abc123xyz789...</code>
                    <span className="key-desc">
                      Dùng để thử nghiệm, không ảnh hưởng dữ liệu thật
                    </span>
                  </div>
                  <div className="key-type live">
                    <span className="key-badge">LIVE</span>
                    <code>vmss_live_def456uvw012...</code>
                    <span className="key-desc">
                      Dùng cho Production, dữ liệu thật
                    </span>
                  </div>
                </div>
              </div>
              <div className="tip-box">
                💡 <strong>Mẹo:</strong> Bắt đầu với Test Key để thử nghiệm an
                toàn!
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="step-card">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>Chọn công cụ gọi API</h3>
              <p>Bạn có thể dùng một trong các cách sau:</p>
              <div className="tools-grid">
                <div className="tool-card recommended">
                  <span className="tool-icon">🌐</span>
                  <strong>Swagger UI</strong>
                  <span className="tool-badge">Khuyên dùng</span>
                  <p>Giao diện web, không cần cài đặt</p>
                  <a
                    href={SWAGGER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tool-link"
                  >
                    Mở Swagger →
                  </a>
                </div>
                <div className="tool-card">
                  <span className="tool-icon">📮</span>
                  <strong>Postman</strong>
                  <p>App desktop phổ biến</p>
                  <a
                    href="https://www.postman.com/downloads/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tool-link"
                  >
                    Tải Postman →
                  </a>
                </div>
                <div className="tool-card">
                  <span className="tool-icon">💻</span>
                  <strong>cURL / Terminal</strong>
                  <p>Dành cho developer</p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="step-card">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>Gửi Request đầu tiên</h3>
              <p>Thử lấy thông tin tài khoản của bạn:</p>
              <div className="code-block">
                <div className="code-header">
                  <span>GET /api/public/v1/user</span>
                  <button
                    className={`copy-btn ${
                      copiedCode === "step3" ? "copied" : ""
                    }`}
                    onClick={() =>
                      copyToClipboard(
                        `curl -X GET "https://api.vmass.vn/api/public/v1/user" \\
  -H "X-API-Key: YOUR_API_KEY_HERE"`,
                        "step3"
                      )
                    }
                  >
                    {copiedCode === "step3" ? "✓ Đã copy" : "📋 Copy"}
                  </button>
                </div>
                <pre>
                  {`curl -X GET "https://api.vmass.vn/api/public/v1/user" \\
  -H "X-API-Key: YOUR_API_KEY_HERE"`}
                </pre>
              </div>
              <div className="response-example">
                <strong>Response:</strong>
                <pre>
                  {`{
  "data": {
    "id": 123,
    "email": "your@email.com",
    "companyName": "Cửa hàng ABC",
    "hotline": "0901234567",
    ...
  }
}`}
                </pre>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="step-card">
            <div className="step-number">4</div>
            <div className="step-content">
              <h3>Tạo đơn hàng đầu tiên</h3>
              <p>Thử tạo một đơn hàng mới:</p>
              <div className="code-block">
                <div className="code-header">
                  <span>POST /api/public/v1/orders</span>
                  <button
                    className={`copy-btn ${
                      copiedCode === "step4" ? "copied" : ""
                    }`}
                    onClick={() =>
                      copyToClipboard(
                        `curl -X POST "https://api.vmass.vn/api/public/v1/orders" \\
  -H "X-API-Key: YOUR_API_KEY_HERE" \\
  -H "Idempotency-Key: order-20260707-0001" \\
  -H "Content-Type: application/json" \\
  -d '{
    "items": [
      { "product_id": 1, "quantity": 2, "price": 50000 }
    ],
    "phone": "0901234567",
    "name": "Nguyễn Văn A",
    "payment_method": "cash"
  }'`,
                        "step4"
                      )
                    }
                  >
                    {copiedCode === "step4" ? "✓ Đã copy" : "📋 Copy"}
                  </button>
                </div>
                <pre>
                  {`curl -X POST "https://api.vmass.vn/api/public/v1/orders" \\
  -H "X-API-Key: YOUR_API_KEY_HERE" \\
  -H "Idempotency-Key: order-20260707-0001" \\
  -H "Content-Type: application/json" \\
  -d '{
    "items": [
      { "product_id": 1, "quantity": 2, "price": 50000 }
    ],
    "phone": "0901234567",
    "name": "Nguyễn Văn A"
  }'`}
                </pre>
              </div>
              <div className="success-box">
                🎉 <strong>Chúc mừng!</strong> Bạn đã tạo đơn hàng đầu tiên qua
                API!
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Endpoints Section */}
      <section id="endpoints" className="section endpoints-section">
        <h2 className="section-title">📚 Danh sách API Endpoints</h2>
        <p className="section-subtitle">
          Tất cả các endpoint có sẵn để tích hợp
        </p>

        <div className="endpoints-tabs">
          <button
            className={activeTab === "orders" ? "active" : ""}
            onClick={() => setActiveTab("orders")}
          >
            📦 Đơn hàng
          </button>
          <button
            className={activeTab === "products" ? "active" : ""}
            onClick={() => setActiveTab("products")}
          >
            🏷️ Sản phẩm
          </button>
          <button
            className={activeTab === "customers" ? "active" : ""}
            onClick={() => setActiveTab("customers")}
          >
            👥 Khách hàng
          </button>
          <button
            className={activeTab === "inventory" ? "active" : ""}
            onClick={() => setActiveTab("inventory")}
          >
            📦 Kho
          </button>
          <button
            className={activeTab === "user" ? "active" : ""}
            onClick={() => setActiveTab("user")}
          >
            👤 Tài khoản
          </button>
        </div>

        <div className="endpoints-content">
          {activeTab === "orders" && (
            <div className="endpoint-group">
              <h3>📦 Đơn hàng (Orders)</h3>
              <table className="endpoints-table">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span className="method get">GET</span>
                    </td>
                    <td>
                      <code>/v1/orders</code>
                    </td>
                    <td>Lấy danh sách đơn hàng (có phân trang)</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method post">POST</span>
                    </td>
                    <td>
                      <code>/v1/orders</code>
                    </td>
                    <td>Tạo đơn hàng mới</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method get">GET</span>
                    </td>
                    <td>
                      <code>/v1/orders/:id</code>
                    </td>
                    <td>Chi tiết đơn hàng (bao gồm các món)</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method get">GET</span>
                    </td>
                    <td>
                      <code>/v1/orders/:id/status</code>
                    </td>
                    <td>Lấy trạng thái đơn hàng</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method patch">PATCH</span>
                    </td>
                    <td>
                      <code>/v1/orders/:id/status</code>
                    </td>
                    <td>Cập nhật trạng thái đơn hàng</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "products" && (
            <div className="endpoint-group">
              <h3>🏷️ Sản phẩm (Products)</h3>
              <table className="endpoints-table">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span className="method get">GET</span>
                    </td>
                    <td>
                      <code>/v1/products</code>
                    </td>
                    <td>Danh sách sản phẩm (có phân trang, lọc)</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method post">POST</span>
                    </td>
                    <td>
                      <code>/v1/products</code>
                    </td>
                    <td>Tạo sản phẩm mới (Live Key only)</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method get">GET</span>
                    </td>
                    <td>
                      <code>/v1/products/:id</code>
                    </td>
                    <td>Chi tiết sản phẩm</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method patch">PATCH</span>
                    </td>
                    <td>
                      <code>/v1/products/:id</code>
                    </td>
                    <td>Cập nhật sản phẩm</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method get">GET</span>
                    </td>
                    <td>
                      <code>/v1/products/:id/variants</code>
                    </td>
                    <td>Danh sách biến thể</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method get">GET</span>
                    </td>
                    <td>
                      <code>/v1/categories</code>
                    </td>
                    <td>Danh sách danh mục</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "customers" && (
            <div className="endpoint-group">
              <h3>👥 Khách hàng (Customers)</h3>
              <table className="endpoints-table">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span className="method get">GET</span>
                    </td>
                    <td>
                      <code>/v1/customers</code>
                    </td>
                    <td>Danh sách khách hàng</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method post">POST</span>
                    </td>
                    <td>
                      <code>/v1/customers</code>
                    </td>
                    <td>Tạo khách hàng mới (Live Key only)</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method get">GET</span>
                    </td>
                    <td>
                      <code>/v1/customers/:id</code>
                    </td>
                    <td>Chi tiết khách hàng</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method patch">PATCH</span>
                    </td>
                    <td>
                      <code>/v1/customers/:id</code>
                    </td>
                    <td>Cập nhật thông tin</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "inventory" && (
            <div className="endpoint-group">
              <h3>📦 Kho / Nguyên liệu (Inventory)</h3>
              <table className="endpoints-table">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span className="method get">GET</span>
                    </td>
                    <td>
                      <code>/v1/inventory</code>
                    </td>
                    <td>Danh sách nguyên liệu/tồn kho</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method get">GET</span>
                    </td>
                    <td>
                      <code>/v1/inventory/low-stock</code>
                    </td>
                    <td>Nguyên liệu sắp hết</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method get">GET</span>
                    </td>
                    <td>
                      <code>/v1/inventory/expiring</code>
                    </td>
                    <td>Nguyên liệu sắp hết hạn</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="method patch">PATCH</span>
                    </td>
                    <td>
                      <code>/v1/inventory/:id/quantity</code>
                    </td>
                    <td>Cập nhật số lượng tồn kho</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "user" && (
            <div className="endpoint-group">
              <h3>👤 Tài khoản (User)</h3>
              <table className="endpoints-table">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span className="method get">GET</span>
                    </td>
                    <td>
                      <code>/v1/user</code>
                    </td>
                    <td>Lấy thông tin tài khoản kết nối</td>
                  </tr>
                </tbody>
              </table>
              <div className="info-box">
                ℹ️ Endpoint này trả về thông tin cơ bản của tài khoản: tên cửa
                hàng, địa chỉ, hotline, thông tin ngân hàng...
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Examples Section */}
      <section id="examples" className="section examples-section">
        <h2 className="section-title">💡 Ví dụ thực tế</h2>
        <p className="section-subtitle">
          Copy và sử dụng ngay các ví dụ dưới đây
        </p>

        <div className="examples-grid">
          <div className="example-card">
            <h4>🛒 Lấy danh sách sản phẩm</h4>
            <div className="code-block">
              <pre>
                {`GET /api/public/v1/products?page=1&limit=20

Response:
{
  "data": [
    { "id": 1, "name": "Cà phê sữa đá", "price": "35000" },
    { "id": 2, "name": "Trà đào", "price": "40000" }
  ],
  "pagination": { "page": 1, "total_pages": 5 }
}`}
              </pre>
            </div>
          </div>

          <div className="example-card">
            <h4>📊 Kiểm tra hàng sắp hết</h4>
            <div className="code-block">
              <pre>
                {`GET /api/public/v1/inventory/low-stock?threshold=10

Response:
{
  "data": [
    { "id": 5, "name": "Sữa tươi", "count": 3, "unit": "lít" },
    { "id": 8, "name": "Đường", "count": 5, "unit": "kg" }
  ]
}`}
              </pre>
            </div>
          </div>

          <div className="example-card">
            <h4>🔄 Cập nhật trạng thái đơn</h4>
            <div className="code-block">
              <pre>
                {`PATCH /api/public/v1/orders/{order_id}/status

Body: { "status": "completed" }

Các trạng thái:
- pending: Chờ xử lý
- processing: Đang làm
- completed: Hoàn thành
- paid: Đã thanh toán
- cancelled: Đã hủy`}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section faq-section">
        <h2 className="section-title">❓ Câu hỏi thường gặp</h2>

        <div className="faq-list">
          <details className="faq-item">
            <summary>Test Key và Live Key khác nhau thế nào?</summary>
            <div className="faq-answer">
              <p>
                <strong>Test Key</strong> (bắt đầu bằng <code>vmss_test_</code>
                ): Dùng để thử nghiệm. Dữ liệu tạo ra là dữ liệu ảo, không ảnh
                hưởng báo cáo doanh thu thật.
              </p>
              <p>
                <strong>Live Key</strong> (bắt đầu bằng <code>vmss_live_</code>
                ): Dùng cho Production. Mọi thao tác đều ảnh hưởng dữ liệu thật
                của cửa hàng.
              </p>
            </div>
          </details>

          <details className="faq-item">
            <summary>Rate limit là bao nhiêu?</summary>
            <div className="faq-answer">
              <p>
                Mặc định: <strong>100 request/phút</strong>. Nếu vượt quá, bạn
                sẽ nhận lỗi <code>429 Too Many Requests</code>.
              </p>
            </div>
          </details>

          <details className="faq-item">
            <summary>Làm sao để biết product_id của sản phẩm?</summary>
            <div className="faq-answer">
              <p>
                Gọi <code>GET /v1/products</code> để lấy danh sách sản phẩm. Mỗi
                sản phẩm sẽ có trường <code>id</code> - đó chính là product_id
                bạn cần.
              </p>
            </div>
          </details>

          <details className="faq-item">
            <summary>Tôi gặp lỗi 401 Unauthorized?</summary>
            <div className="faq-answer">
              <p>Kiểm tra lại:</p>
              <ul>
                <li>
                  Header <code>X-API-Key</code> đã được thêm chưa?
                </li>
                <li>API Key có đúng không (copy lại từ Dashboard)?</li>
                <li>API Key có hết hạn hoặc bị vô hiệu hóa không?</li>
              </ul>
            </div>
          </details>

          <details className="faq-item">
            <summary>Làm sao để xóa dữ liệu test?</summary>
            <div className="faq-answer">
              <p>
                Gọi <code>POST /v1/sandbox/reset</code> với Test Key để xóa toàn
                bộ đơn hàng, bill test đã tạo.
              </p>
            </div>
          </details>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2>Sẵn sàng tích hợp?</h2>
        <p>Bắt đầu ngay với Swagger UI - không cần code!</p>
        <div className="cta-buttons">
          <a
            href={SWAGGER_URL}
            className="api-btn-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            🚀 Mở Swagger UI
          </a>
          <a href="mailto:hotro@vmass.vn" className="api-btn-secondary">
            📧 Liên hệ hỗ trợ
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="api-docs-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img
              src="/assets/img/logo-horizontal.svg"
              alt="VMASS"
              className="footer-logo-img"
            />
            <span className="logo-api-suffix">API</span>
          </div>
          <div className="footer-links">
            <a
              href="https://vmass.vn"
              target="_blank"
              rel="noopener noreferrer"
            >
              VMASS.vn
            </a>
            <a href="mailto:hotro@vmass.vn">hotro@vmass.vn</a>
            <a href="tel:0708245246">0708 245 246</a>
          </div>
          <div className="footer-copy">© 2026 VMASS. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
};

export default ApiDocsLanding;
